<script lang="ts">
    import { _ } from "svelte-i18n"
    import Cart from "../../layout/Cart.svelte"
    import CheckoutFooter from "./CheckoutFooter.svelte"
    import FormInputText from "../../widgets/FormInputText.svelte"
    import FormInputCheckbox from "../../widgets/FormInputCheckbox.svelte"
    import { cart, cartOverlay, checkoutData, login, location, getAsyncCartSum } from "../../../store"
    import { navigate } from "../../../../../vendor/svelte-routing"
    import { getCountry, postOrder } from "../../../api"
    import MiniLoader from "../../widgets/MiniLoader.svelte"
    import { setUser } from "../../../sentry"
    import ShippingDeliveryAddress from "./ShippingDeliveryAddress.svelte"
    import { maxInputLength } from "../../../../../api/hooks/config-client"

    let cancellationNotice = false
    let privacyNotice = false
    let conditionsNotice = false
    let loading = false

    let dropShipment = false

    location.subscribe((l) => {
        if (l?.path == "/checkout/dealer") {
            // close overlay because cart is also in this overview
            $cartOverlay = false
        }
    })

    $: if (!$cart?.length || !$login) {
        // empty cart goto start
        navigate("/", { replace: true })
    }

    const submitOrder = async () => {
        try {
            // track in sentry
            setUser({
                username:
                    $login?.account?.ident &&
                    $login.account.ident.dealerNumber + "-" + $login.account.ident.personNumber,
                email: $checkoutData.contact?.email,
            })

            // restrict contact, deliveryAddress and billingAddress object to max length
            for (const key in $checkoutData.contact) {
                // @ts-ignore
                if ($checkoutData.contact[key]?.length > maxInputLength[key]) {
                    // @ts-ignore
                    $checkoutData.contact[key] = $checkoutData.contact[key].substring(0, maxInputLength[key])
                }
            }
            for (const key in $checkoutData.deliveryAddress) {
                // @ts-ignore
                if ($checkoutData.deliveryAddress[key]?.length > maxInputLength[key]) {
                    // @ts-ignore
                    $checkoutData.deliveryAddress[key] = $checkoutData.deliveryAddress[key].substring(
                        0,
                        // @ts-ignore
                        maxInputLength[key]
                    )
                }
            }
            for (const key in $checkoutData.billingAddress) {
                // @ts-ignore
                if ($checkoutData.billingAddress[key]?.length > maxInputLength[key]) {
                    // @ts-ignore
                    $checkoutData.billingAddress[key] = $checkoutData.billingAddress[key].substring(
                        0,
                        // @ts-ignore
                        maxInputLength[key]
                    )
                }
            }

            const deliveryCountry = await getCountry(
                dropShipment ? $checkoutData.deliveryAddress?.countryCode || "DE" : "DE"
            )

            const orderBaseData: Order = {
                dealerNumber: $login?.account?.ident?.dealerNumber?.toString(),
                contact: $checkoutData.contact?.email ? { email: $checkoutData.contact.email } : undefined,
                deliveryCountry,
                commission: $checkoutData.commission ? $checkoutData.commission : undefined,
                payment: {
                    type: "invoice",
                },
                deliveryAddress: dropShipment ? $checkoutData.deliveryAddress : undefined,
                cart: [],
                sum: null,
            }

            // split carts to seperate orders
            const carts: CartItem[][] = [
                $cart.filter((i) => !i.product.price_net_dealer_is_fixed),
                $cart.filter((i) => i.product.price_net_dealer_is_fixed),
            ]

            for (let i = 0; i < carts.length; i++) {
                const cartItems = carts[i]

                if (cartItems.length) {
                    // post order
                    await postOrder({
                        ...orderBaseData,
                        cart: cartItems,
                        sum: await getAsyncCartSum(cartItems),
                        // flag type of cart
                        packaging: cartItems[0].product?.price_net_dealer_is_fixed,
                        bonus: !cartItems[0].product?.price_net_dealer_is_fixed,
                    })
                }
            }

            typeof window !== "undefined" &&
                window.setTimeout(() => {
                    $cart = []
                }, 1000)

            navigate("/checkout/confirmation")
        } catch (e) {
            console.log(e)
            alert($_("checkout.error-alert"))
            $checkoutData.stepsValid = 0
            throw new Error(JSON.stringify(e, null, 4))
        }
    }

    function submitClick() {
        if (!loading) {
            loading = true
            submitOrder()
        }
    }

    let invalidProductsInCart: boolean
</script>

<div class="subline">{$_("checkout.order")}</div>
<h2 class="h2_nooffset">{$_("checkout.summary")}</h2>

<form on:submit|preventDefault="{submitClick}">
    <section>
        <div class="section_headline">{$_("checkout.cart")}</div>
        <Cart checkoutButton="{false}" bind:invalidProductsInCart />
    </section>

    {#if !invalidProductsInCart}
        <section>
            <div class="section_headline">
                {$_("checkout.delivery-and-billing-address")}
            </div>
            <div class="row">
                <div class="col-md-12">
                    <FormInputText
                        name="dealerNumber"
                        placeholder="{$_('checkout.dealer-number')}"
                        label="{$_('checkout.dealer-number')}"
                        disabled
                        value="{$login?.account?.ident?.dealerNumber}"
                    />
                </div>
            </div>
            <div class="row">
                <div class="col-md-12">
                    {@html $_("checkout.dealer-address-info-html")}
                </div>
            </div>
            <div class="row">
                <div class="col-md-12">
                    <FormInputCheckbox label="{$_('checkout.drop-shipment-address')}" bind:checked="{dropShipment}" />
                </div>
                {#if dropShipment}
                    <div class="col-md-12">
                        <ShippingDeliveryAddress hideHeadline />
                    </div>
                {/if}
            </div>
        </section>
        <section>
            <div class="section_headline">{$_("checkout.further-data")}</div>
            <div class="row">
                <div class="col-md-12">
                    {$_("checkout.dealer-email-prompt")}
                    <FormInputText
                        name="email"
                        placeholder="{$_('checkout.email')}"
                        bind:value="{$checkoutData.contact.email}"
                        type="email"
                        maxlength="{maxInputLength.email}"
                    />
                </div>
                <div class="col-md-12">
                    {$_("checkout.dealer-commission-prompt")}
                    <FormInputText
                        name="commission"
                        placeholder="{$_('checkout.commission')}"
                        bind:value="{$checkoutData.commission}"
                        type="text"
                        maxlength="{maxInputLength.commission}"
                    />
                </div>
            </div>
        </section>
        <section style="border-bottom:dotted 1px var(--border-color-2);">
            <div class="section_headline">{$_("checkout.payment")}</div>
            <p>{$_("checkout.invoice")}</p>
        </section>

        <section>
            <FormInputCheckbox label="{$_('checkout.store-my-data')}" bind:checked="{$checkoutData.localStorage}" />
            <FormInputCheckbox label="" bind:checked="{cancellationNotice}">
                {@html $_("checkout.right-of-withdrawal-checkbox-html")}
            </FormInputCheckbox>
            <FormInputCheckbox label="" bind:checked="{privacyNotice}">
                {@html $_("checkout.privacy-terms-checkbox-html")}
            </FormInputCheckbox>
            <FormInputCheckbox label="" bind:checked="{conditionsNotice}">
                {@html $_("checkout.general-terms-checkbox-html")}
            </FormInputCheckbox>
        </section>
        {#if loading}
            <MiniLoader />
        {:else}
            <CheckoutFooter
                nexttitle="{$_('checkout.order-now')}"
                submitDisabled="{!cancellationNotice || !privacyNotice || !conditionsNotice}"
            />
        {/if}
    {/if}
</form>
